import { PersonCard } from '@/components/features/People/tables/PersonCard';
import { PersonTable } from '@/components/features/People/tables/PersonTable';
import { CreateButton } from '@/components/layout/CreateButton';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs';
import { useDebounce } from '@/hooks/use-debounce';
import { useIndexFilters, type FilterSpecMap } from '@/hooks/use-index-filters';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PaginationMeta, type People } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface CompanyOption {
    id: string;
    name: string;
}

interface PeoplePageProps {
    people: People[];
    pagination: PaginationMeta;
    filters: {
        search?: string;
        company_id?: string | string[];
        accounting?: string;
        per_page?: number;
        page?: number;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
    };
    companies: CompanyOption[];
    availableFilterValues?: {
        company_id: string[];
    };
    [key: string]: unknown;
}

export default function People() {
    const { props } = usePage<PeoplePageProps>();
    const { people, pagination, filters, companies, availableFilterValues } = props;
    const { t } = useTranslations();
    const breadcrumbs = useBreadcrumbs('people');

    /**
     * Column id -> query parameter. The ids must match `columnDefinitions` and
     * `PersonTable`'s column ids, or the filter silently stops rendering.
     */
    const filterSpecs = useMemo<FilterSpecMap>(
        () => ({
            company: { param: 'company_id' },
            accounting: { param: 'accounting' },
        }),
        [],
    );

    const {
        columnFilters,
        handleFilterChange,
        toggleSingleFilter,
        clearFilters: clearServerFilters,
        applyFilters,
        loading,
    } = useIndexFilters(filterSpecs, filters, '/people');

    const isMobile = useIsMobile();
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [searchValue, setSearchValue] = useState(filters.search?.toString() ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [sortColumn, setSortColumn] = useState<string | null>(filters.sort_by ?? null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
        filters.sort_order === 'asc' || filters.sort_order === 'desc' ? filters.sort_order : null,
    );
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(['full_name', 'position', 'email', 'phone', 'company', 'accounting']));
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);

    const columnDefinitions = [
        { id: 'full_name', label: t('people.index.columns.full_name', 'Full Name') },
        { id: 'position', label: t('people.index.columns.position', 'Position') },
        { id: 'email', label: t('people.index.columns.email', 'Email') },
        { id: 'phone', label: t('people.index.columns.phone', 'Phone') },
        { id: 'company', label: t('people.index.columns.company', 'Company') },
        { id: 'accounting', label: t('people.index.columns.accounting', 'Accounting') },
    ];

    const quickFilterDefinitions = [{ id: 'accounting', label: t('people.index.quick_filters.accounting', 'Accounting') }];

    useEffect(() => {
        setViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    useEffect(() => {
        const nextSearch = filters.search?.toString() ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search?.toString() ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({
            search: debouncedSearchValue || undefined,
            page: 1,
        });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    /**
     * Options come from whole-dataset lists, not from the rows on screen.
     *
     * Name, position, email and phone are deliberately absent: they are near-unique per
     * contact, so a checkbox list of them is useless, and three of the four are encrypted
     * at rest and cannot be filtered in SQL. The search box covers them, server-side.
     */
    const { columnFilterOptions } = useMemo(() => {
        const availableCompanyIds = new Set(availableFilterValues?.company_id ?? []);
        const companyOptions = availableCompanyIds.size ? companies.filter((company) => availableCompanyIds.has(company.id)) : companies;

        return {
            columnFilterOptions: {
                company: companyOptions.map((company) => ({ value: company.id, label: company.name })),
                accounting: [
                    { value: 'true', label: t('common.yes', 'Yes') },
                    { value: 'false', label: t('common.no', 'No') },
                ],
            } as Record<string, Array<{ value: string; label: string }>>,
        };
    }, [companies, availableFilterValues, t]);

    const quickFilterNames = quickFilterDefinitions.filter((q) => (columnFilters[q.id] || []).length > 0).map((q) => q.label);
    const quickFilterButtonLabel =
        quickFilterNames.length > 0
            ? `${t('people.index.toolbar.quick_filters', 'Quick filters')}: ${quickFilterNames.join(', ')}`
            : t('people.index.filters.all_people', 'All People');

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        // Keys must match PersonController::SORTABLE_COLUMNS — names and emails are
        // encrypted at rest, so the database cannot order by them.
        const sortByMap: Record<string, string> = {
            position: 'position',
            accounting: 'accounting',
        };
        applyFilters({ sort_by: sortByMap[column] ?? 'created_at', sort_order: direction, page: 1 });
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
        applyFilters({ sort_by: undefined, sort_order: undefined, page: 1 });
    };

    const clearFilters = () => {
        setSearchValue('');
        clearServerFilters();
    };

    const handlePageChange = (page: number) => {
        applyFilters({ page });
    };

    const handlePerPageChange = (perPage: number) => {
        applyFilters({ per_page: perPage, page: 1 });
    };

    const handleColumnHide = (column: string) => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            next.delete(column);
            return next;
        });
    };

    const handleColumnShow = (column: string) => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            next.add(column);
            return next;
        });
    };

    // Inline editing handlers
    const handleUpdate = {
        company: async (personId: string, companyId: string | null) => {
            router.put(
                `/person/${personId}`,
                {
                    company_id: companyId || null,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('people.index.messages.company_updated', 'Company updated'));
                    },
                    onError: () => {
                        toast.error(t('people.index.messages.failed_update_company', 'Failed to update company'));
                    },
                },
            );
        },
        position: async (personId: string, value: string | null) => {
            router.put(
                `/person/${personId}`,
                {
                    position: value || null,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('people.index.messages.position_updated', 'Position updated'));
                    },
                    onError: () => {
                        toast.error(t('people.index.messages.failed_update_position', 'Failed to update position'));
                    },
                },
            );
        },
        accounting: async (personId: string, value: boolean) => {
            router.put(
                `/person/${personId}`,
                {
                    accounting: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('people.index.messages.accounting_updated', 'Accounting status updated'));
                    },
                    onError: () => {
                        toast.error(t('people.index.messages.failed_update_accounting', 'Failed to update accounting status'));
                    },
                },
            );
        },
        email: async (personId: string, value: string | null) => {
            router.put(
                `/person/${personId}`,
                {
                    email: value || null,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('people.index.messages.email_updated', 'Email updated'));
                    },
                    onError: () => {
                        toast.error(t('people.index.messages.failed_update_email', 'Failed to update email'));
                    },
                },
            );
        },
        phone: async (personId: string, value: string | null) => {
            router.put(
                `/person/${personId}`,
                {
                    phone: value || null,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('people.index.messages.phone_updated', 'Phone updated'));
                    },
                    onError: () => {
                        toast.error(t('people.index.messages.failed_update_phone', 'Failed to update phone'));
                    },
                },
            );
        },
    };

    // Only unencrypted columns — the database cannot order by an encrypted name or email.
    const sortableColumns = [{ id: 'position', label: t('people.index.columns.position', 'Position') }];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('people.index.title', 'Contacts')} />
            <PageHeader
                title={t('people.index.heading', 'Contacts')}
                subtitle={t('people.index.subheading', 'Manage your contacts and their details')}
                actions={
                    <CreateButton href="/people/create" permission="contacts.create" label={t('people.index.actions.create', 'Create Contact')} />
                }
            />

            <PageContent>
                {/* Toolbar */}
                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel}
                    quickFilterDefinitions={quickFilterDefinitions}
                    columnDefinitions={columnDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={searchValue}
                    setSearch={setSearchValue}
                    perPage={pagination.per_page}
                    onPerPageChange={handlePerPageChange}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={pagination.total}
                    resourceName="people"
                    resourceNamePlural="people"
                    sortableColumns={sortableColumns}
                />

                {/* Content */}
                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {people.map((person) => (
                            <PersonCard key={person.id} person={person} />
                        ))}
                    </div>
                ) : (
                    <PersonTable
                        people={people}
                        companies={companies}
                        onUpdate={handleUpdate}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        onColumnHide={handleColumnHide}
                        onColumnShow={handleColumnShow}
                        visibleColumns={visibleColumns}
                        columnFilters={columnFilters}
                        columnFilterOptions={columnFilterOptions}
                        onFilterChange={handleFilterChange}
                    />
                )}

                <PaginationControls
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onPerPageChange={handlePerPageChange}
                    loading={loading}
                    className="mt-6"
                />

                {viewMode === 'grid' && people.length === 0 && (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground">
                                {t('people.index.empty.no_people_found', 'No people found matching your filters.')}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </PageContent>
        </AppLayout>
    );
}
